/**
 * User Management Page.
 *
 * Comprehensive admin interface for managing users, custom roles,
 * permission sets, and teams. Supports the layered permission model:
 *
 * Layer 1: Role (base permissions from custom role)
 * Layer 2: Permission Sets (additive bundles stacked on role)
 * Layer 3: User Overrides (individual grants and revocations)
 *
 * Also manages teams for visibility scoping and field-level visibility
 * rules for hiding sensitive data (prices, costs, margins).
 */

import { useState } from "react";
import {
  Users,
  Plus,
  Shield,
  ShieldCheck,
  UserCog,
  Users2,
  Check,
  X,
  Eye,
  EyeOff,
  Pencil,
  Trash2,
  Mail,
  KeyRound,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import { useAuth } from "@/hooks/useAuth";
import type { PaginatedResponse } from "@/types/common";
import type { User } from "@/types/user";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import ActionDrawer from "@/components/ui/ActionDrawer";
import PermissionChipGrid from "@/features/permissions/PermissionChips";
import { usePermissionCatalogue } from "@/features/permissions/useCatalogue";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

type Tab = "users" | "roles" | "sets" | "teams";

interface CustomRole {
  id: string;
  name: string;
  description: string | null;
  base_permissions: string[];
  is_system: boolean;
  is_admin: boolean;
  is_owner: boolean;
  parent_role_id: string | null;
  /** base_permissions plus everything the parent chain grants. Null with no parent. */
  effective_permissions: string[] | null;
}
interface PermSet {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
}
interface Team {
  id: string;
  name: string;
  description: string | null;
  manager_id: string | null;
  parent_team_id: string | null;
}
function UserManagementPage() {
  const [activeTab, setActiveTab] = useState<Tab>("users");

  const tabs: { key: Tab; label: string; icon: React.ElementType }[] = [
    { key: "users", label: "Users", icon: Users },
    { key: "roles", label: "Roles", icon: Shield },
    { key: "sets", label: "Permission Sets", icon: ShieldCheck },
    { key: "teams", label: "Teams", icon: Users2 },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          User Management
        </h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Manage users, roles, permission sets, and teams.
        </p>
      </div>

      <div className="border-b border-[var(--border-subtle)]">
        <nav className="flex gap-0.5 overflow-x-auto pb-px scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`group relative flex items-center gap-2 shrink-0 px-4 pb-3 pt-1 text-[13px] font-semibold transition-all duration-200 ${
                activeTab === tab.key
                  ? "text-[var(--color-interactive-primary)]"
                  : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              }`}
            >
              <tab.icon
                className={`h-4 w-4 transition-colors duration-200 ${activeTab === tab.key ? "" : "opacity-50 group-hover:opacity-80"}`}
              />
              {tab.label}
              {activeTab === tab.key && (
                <span
                  className="absolute bottom-0 left-2 right-2 h-[2.5px] rounded-full bg-gradient-to-r from-[var(--color-interactive-primary)] to-brand-400"
                  style={{ boxShadow: "0 1px 4px oklch(62% 0.22 285 / 0.3)" }}
                />
              )}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "users" && <UsersTab />}
      {activeTab === "roles" && <RolesTab />}
      {activeTab === "sets" && <PermissionSetsTab />}
      {activeTab === "teams" && <TeamsTab />}
    </div>
  );
}

// ==========================================================================
// USERS TAB
// ==========================================================================

function UsersTab() {
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();

  const { data: userData, isLoading } = useQuery<PaginatedResponse<User>>({
    queryKey: ["admin-users"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/users?page_size=100")).data,
  });
  const { data: rolesData } = useQuery<CustomRole[]>({
    queryKey: ["custom-roles"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/permissions/roles")).data,
  });
  const { data: setsData } = useQuery<PermSet[]>({
    queryKey: ["permission-sets"],
    queryFn: async () => (await apiClient.get("/api/v1/permissions/sets")).data,
  });
  const { data: teamsData } = useQuery<Team[]>({
    queryKey: ["teams"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/permissions/teams")).data,
  });

  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [createName, setCreateName] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createRole, setCreateRole] = useState("viewer");
  const [createCanSell, setCreateCanSell] = useState(false);
  const [createCommissionRate, setCreateCommissionRate] = useState("");
  const [createHourlyRate, setCreateHourlyRate] = useState("");

  const createMutation = useMutation({
    mutationFn: async () =>
      // No password: the person chooses their own from the invitation the
      // server sends (FS-012). An administrator never knows it.
      (
        await apiClient.post<
          User & {
            invitation_email_status: string | null;
            invitation_message: string | null;
          }
        >("/api/v1/auth/register", {
          email: createEmail,
          full_name: createName,
          role: createRole,
          can_sell: createCanSell,
          default_commission_rate:
            createCanSell && createCommissionRate
              ? parseFloat(createCommissionRate) / 100
              : undefined,
          hourly_rate: createHourlyRate
            ? parseFloat(createHourlyRate)
            : undefined,
        })
      ).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      setShowCreate(false);
      setCreateName("");
      setCreateEmail("");
      setCreateCanSell(false);
      setCreateCommissionRate("");
      setCreateHourlyRate("");
      // "queued" means no email provider: nothing was sent, and nothing will
      // re-send it. Only sent/delivered is success.
      if (
        ["sent", "delivered"].includes(created.invitation_email_status ?? "")
      ) {
        toast.success(
          "Invitation sent",
          `${created.full_name} will get an email to choose a password.`,
        );
      } else {
        toast.error(
          "User added, invitation not sent",
          created.invitation_message ??
            "Use Resend invite once email is working.",
        );
      }
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error("Failed to invite user", msg || "");
    },
  });

  // Invite (or resend) for someone who has never set a password; reset for
  // everyone else. The server decides which applies and says so if asked the
  // wrong one, so these only choose which button to show.
  const linkMutation = useMutation({
    mutationFn: async ({
      id,
      kind,
    }: {
      id: string;
      kind: "invite" | "password-reset";
    }) =>
      (
        await apiClient.post<{ message: string; email_status: string }>(
          `/api/v1/users/${id}/${kind}`,
        )
      ).data,
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      if (["sent", "delivered"].includes(result.email_status)) {
        toast.success(result.message);
      } else {
        toast.error("Email not sent", result.message);
      }
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error("Couldn't send the link", msg || "");
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/users/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("User deactivated");
    },
  });

  const users = userData?.items || [];
  const roles = rolesData || [];
  const sets = setsData || [];
  const teams = teamsData || [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" /> Invite User
        </Button>
      </div>

      <ActionDrawer
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Invite a user"
        description="They'll get an email to choose their own password. The link lasts seven days."
        icon={Users}
        size="sm"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
          className="space-y-4"
        >
          <Input
            label="Full Name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            required
            placeholder="Jane Smith"
          />
          <Input
            label="Email"
            type="email"
            value={createEmail}
            onChange={(e) => setCreateEmail(e.target.value)}
            required
            placeholder="jane@company.com"
          />
          <div>
            <label className="block text-[13px] font-medium text-[var(--text-primary)]">
              Role
            </label>
            <select
              value={createRole}
              onChange={(e) => setCreateRole(e.target.value)}
              className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
            >
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="technician">Technician</option>
              <option value="sales">Sales</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <Input
            label="Hourly Rate ($)"
            type="number"
            step="0.01"
            value={createHourlyRate}
            onChange={(e) => setCreateHourlyRate(e.target.value)}
            placeholder="0.00"
          />
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-[var(--text-primary)] cursor-pointer">
              <input
                type="checkbox"
                checked={createCanSell}
                onChange={(e) => setCreateCanSell(e.target.checked)}
                className="rounded border-[var(--border-default)]"
              />
              Can sell equipment
            </label>
          </div>
          {createCanSell && (
            <Input
              label="Default Commission Rate (%)"
              type="number"
              step="0.01"
              value={createCommissionRate}
              onChange={(e) => setCreateCommissionRate(e.target.value)}
              placeholder="5.00"
            />
          )}
          <div className="flex justify-end pt-2">
            <Button type="submit" isLoading={createMutation.isPending}>
              <Mail className="h-4 w-4" /> Send invitation
            </Button>
          </div>
        </form>
      </ActionDrawer>

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : (
        <div className="space-y-3">
          {users.map((u) => (
            <Card key={u.id} padding="sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-200 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300 text-sm font-semibold">
                    {u.full_name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[var(--text-primary)]">
                        {u.full_name}
                      </span>
                      <Badge
                        variant={
                          u.role === "owner" || u.role === "admin"
                            ? "danger"
                            : u.role === "manager"
                              ? "warning"
                              : "default"
                        }
                      >
                        {u.role}
                      </Badge>
                      {u.password_changed_at === null ? (
                        <Badge variant="info">
                          {u.is_active ? "Invited" : "Not invited"}
                        </Badge>
                      ) : (
                        !u.is_active && <Badge variant="danger">Inactive</Badge>
                      )}
                      {u.can_sell && <Badge variant="success">Sales</Badge>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[var(--text-tertiary)]">
                      <span>{u.email}</span>
                      {u.hourly_rate != null && (
                        <span>${Number(u.hourly_rate)}/hr</span>
                      )}
                      {u.can_sell && u.default_commission_rate != null && (
                        <span>
                          {(Number(u.default_commission_rate) * 100).toFixed(1)}
                          % commission
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {u.id !== currentUser?.id &&
                    (u.password_changed_at === null ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        title={
                          u.is_active
                            ? "Send a fresh invitation; the earlier link stops working"
                            : "Turn this account on and email an invitation to choose a password"
                        }
                        isLoading={
                          linkMutation.isPending &&
                          linkMutation.variables?.id === u.id
                        }
                        onClick={() =>
                          linkMutation.mutate({ id: u.id, kind: "invite" })
                        }
                      >
                        <Mail className="h-3.5 w-3.5" />{" "}
                        {u.is_active ? "Resend invite" : "Invite"}
                      </Button>
                    ) : (
                      u.is_active && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Email a one-hour link to choose a new password. You never see the password."
                          isLoading={
                            linkMutation.isPending &&
                            linkMutation.variables?.id === u.id
                          }
                          onClick={() => {
                            if (
                              confirm(
                                `Email ${u.full_name} a link to reset their password?`,
                              )
                            )
                              linkMutation.mutate({
                                id: u.id,
                                kind: "password-reset",
                              });
                          }}
                        >
                          <KeyRound className="h-3.5 w-3.5" /> Reset password
                        </Button>
                      )
                    ))}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setEditingUser(editingUser === u.id ? null : u.id)
                    }
                  >
                    <UserCog className="h-3.5 w-3.5" /> Permissions
                  </Button>
                  {u.id !== currentUser?.id && u.role !== "owner" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (confirm(`Deactivate ${u.full_name}?`))
                          deactivateMutation.mutate(u.id);
                      }}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              {/* User Permission Editor */}
              {editingUser === u.id && (
                <UserPermissionEditor
                  userId={u.id}
                  userName={u.full_name}
                  roles={roles}
                  sets={sets}
                  teams={teams}
                />
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// USER PERMISSION EDITOR (inline panel)
// ==========================================================================

function UserPermissionEditor({
  userId,
  userName,
  roles,
  sets,
  teams,
}: {
  userId: string;
  userName: string;
  roles: CustomRole[];
  sets: PermSet[];
  teams: Team[];
}) {
  const qc = useQueryClient();
  const { data: effective, isLoading } = useQuery<{
    effective_permissions: string[];
    sources: Array<{ permission: string; source: string; granted: boolean }>;
    role_id: string | null;
    permission_set_ids: string[] | null;
    permission_grants: string[] | null;
    permission_revocations: string[] | null;
    team_id: string | null;
    visibility_scope: string;
    field_visibility: Record<string, string> | null;
  }>({
    queryKey: ["user-effective-perms", userId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/permissions/users/${userId}/effective`))
        .data,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (
        await apiClient.put(
          `/api/v1/permissions/users/${userId}/permissions`,
          data,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user-effective-perms", userId] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Permissions updated");
    },
    onError: () => toast.error("Failed to update permissions"),
  });

  if (isLoading || !effective) return <LoadingSpinner className="py-4" />;

  const currentSets = effective.permission_set_ids || [];

  return (
    <div className="mt-4 pt-4 border-t border-[var(--border-subtle)] space-y-4">
      <UserProfileFields userId={userId} />

      <h4 className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
        Permissions for {userName}
      </h4>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {/* Role */}
        <div>
          <label className="block text-[13px] font-medium text-[var(--text-primary)]">
            Role
          </label>
          <select
            value={effective.role_id || ""}
            onChange={(e) =>
              updateMutation.mutate({ role_id: e.target.value || null })
            }
            className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
          >
            <option value="">Use legacy role</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.is_owner ? " (Owner)" : r.is_admin ? " (Admin)" : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Team */}
        <div>
          <label className="block text-[13px] font-medium text-[var(--text-primary)]">
            Team
          </label>
          <select
            value={effective.team_id || ""}
            onChange={(e) =>
              updateMutation.mutate({ team_id: e.target.value || null })
            }
            className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
          >
            <option value="">No team</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {/* Visibility Scope */}
        <div>
          <label className="block text-[13px] font-medium text-[var(--text-primary)]">
            Visibility
          </label>
          <select
            value={effective.visibility_scope}
            onChange={(e) =>
              updateMutation.mutate({ visibility_scope: e.target.value })
            }
            className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
          >
            <option value="all">All records</option>
            <option value="team">Team records only</option>
            <option value="own">Own records only</option>
          </select>
        </div>
      </div>

      {/* Permission Sets */}
      <div>
        <label className="block text-[13px] font-medium text-[var(--text-primary)] mb-1.5">
          Permission Sets
        </label>
        <div className="flex flex-wrap gap-2">
          {sets.map((s) => {
            const isActive = currentSets.includes(s.id);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  const next = isActive
                    ? currentSets.filter((id) => id !== s.id)
                    : [...currentSets, s.id];
                  updateMutation.mutate({ permission_set_ids: next });
                }}
                className={`flex items-center gap-1.5 rounded-[var(--radius-pill)] px-3 py-1.5 text-xs font-semibold border transition-all ${
                  isActive
                    ? "bg-[var(--color-interactive-primary)]/10 text-[var(--color-interactive-primary)] border-[var(--color-interactive-primary)]/30"
                    : "bg-transparent text-[var(--text-tertiary)] border-[var(--border-default)] hover:border-[var(--border-strong)]"
                }`}
              >
                {isActive ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <Plus className="h-3 w-3" />
                )}
                {s.name}
              </button>
            );
          })}
          {sets.length === 0 && (
            <p className="text-xs text-[var(--text-tertiary)]">
              No permission sets created yet.
            </p>
          )}
        </div>
      </div>

      {/* Field Visibility */}
      <div>
        <label className="block text-[13px] font-medium text-[var(--text-primary)] mb-1.5">
          Field Visibility
        </label>
        <div className="flex flex-wrap gap-2">
          {[
            "prices",
            "costs",
            "margins",
            "commissions",
            "financial_summary",
          ].map((field) => {
            const current = effective.field_visibility?.[field] || "visible";
            const isHidden = current === "hidden";
            return (
              <button
                key={field}
                type="button"
                onClick={() => {
                  const fv = { ...(effective.field_visibility || {}) };
                  fv[field] = isHidden ? "visible" : "hidden";
                  updateMutation.mutate({ field_visibility: fv });
                }}
                className={`flex items-center gap-1.5 rounded-[var(--radius-pill)] px-3 py-1.5 text-xs font-semibold border transition-all ${
                  isHidden
                    ? "bg-[var(--fill-danger)] text-[var(--text-danger)] border-[var(--text-danger)]/20"
                    : "bg-transparent text-[var(--text-tertiary)] border-[var(--border-default)] hover:border-[var(--border-strong)]"
                }`}
              >
                {isHidden ? (
                  <EyeOff className="h-3 w-3" />
                ) : (
                  <Eye className="h-3 w-3" />
                )}
                {field.replace("_", " ")}
              </button>
            );
          })}
        </div>
      </div>

      {/* Effective Permissions Summary */}
      <div>
        <label className="block text-[13px] font-medium text-[var(--text-primary)] mb-1.5">
          Effective Permissions ({effective.effective_permissions.length})
        </label>
        <div className="flex flex-wrap gap-1">
          {effective.effective_permissions.map((p) => (
            <span
              key={p}
              className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--surface-sunken)] text-[var(--text-secondary)]"
            >
              {p}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// ROLES TAB
// ==========================================================================

function RolesTab() {
  const qc = useQueryClient();
  const { data: roles, isLoading } = useQuery<CustomRole[]>({
    queryKey: ["custom-roles"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/permissions/roles")).data,
  });

  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [parentId, setParentId] = useState("");
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());

  const createMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post("/api/v1/permissions/roles", {
          name,
          description: desc || undefined,
          base_permissions: Array.from(selectedPerms),
          is_admin: isAdmin,
          parent_role_id: parentId || null,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["custom-roles"] });
      setShowCreate(false);
      setName("");
      setDesc("");
      setParentId("");
      setSelectedPerms(new Set());
      toast.success("Role created");
    },
    // The server refuses a loop, a parent in another tenant, and anything
    // beyond the caller's own permissions — each with a sentence saying which.
    // Replacing them with "Failed to create role" is how a user ends up
    // guessing which of the three they hit.
    onError: (err: unknown) =>
      toast.error(
        "Failed to create role",
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "",
      ),
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Record<string, unknown>;
    }) => (await apiClient.put(`/api/v1/permissions/roles/${id}`, data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["custom-roles"] });
      setEditingId(null);
      toast.success("Role updated");
    },
    // Same as create: the refusal sentence is the whole value. A system role
    // can be narrowed but not widened, and the server says what the change
    // would have added.
    onError: (err: unknown) =>
      toast.error(
        "Failed to update role",
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "",
      ),
  });

  // Deleting a role is a two-step flow when anyone holds it. The API returns
  // 409 with a user_count rather than cascading, because users.role_id is
  // ON DELETE SET NULL — a silent delete would drop every holder back to
  // their legacy role string with nothing to show for it.
  const [deleting, setDeleting] = useState<CustomRole | null>(null);
  const [holderCount, setHolderCount] = useState(0);
  const [replacementId, setReplacementId] = useState("");

  const deleteMutation = useMutation({
    mutationFn: async ({
      id,
      replacement,
    }: {
      id: string;
      replacement?: string;
    }) => {
      const query = replacement ? `?replacement_role_id=${replacement}` : "";
      await apiClient.delete(`/api/v1/permissions/roles/${id}${query}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["custom-roles"] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["user-effective-perms"] });
      setDeleting(null);
      setReplacementId("");
      toast.success("Role deleted");
    },
    onError: (err: unknown) => {
      const res = (
        err as {
          response?: {
            status?: number;
            data?: { detail?: string; user_count?: number };
          };
        }
      )?.response;
      if (res?.status === 409 && res.data?.user_count) {
        // Not a failure — the API is asking where these people should go.
        setHolderCount(res.data.user_count);
        return;
      }
      setDeleting(null);
      toast.error("Failed to delete role", res?.data?.detail || "");
    },
  });

  const { groups, catalogue } = usePermissionCatalogue();

  const startDelete = (r: CustomRole) => {
    setDeleting(r);
    setHolderCount(0);
    setReplacementId("");
    deleteMutation.mutate({ id: r.id });
  };

  const startEdit = (r: CustomRole) => {
    setEditingId(r.id);
    setName(r.name);
    setDesc(r.description || "");
    setIsAdmin(r.is_admin);
    setParentId(r.parent_role_id || "");
    setSelectedPerms(new Set(r.base_permissions));
    setShowCreate(false);
  };

  const togglePerm = (p: string) => {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const toggleGroup = (perms: string[]) => {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      const allSelected = perms.every((p) => next.has(p));
      if (allSelected) perms.forEach((p) => next.delete(p));
      else perms.forEach((p) => next.add(p));
      return next;
    });
  };

  // Inheritance (FS-007b). A role inherits everything its parent grants, so
  // the shared part of two overlapping roles is declared once — a Sales
  // Manager is a Salesperson plus pricing authority. The editor shows what
  // comes in from the parent as a read-only line rather than pre-ticking those
  // chips, because a ticked chip would look like something this role grants
  // directly and removing it would do nothing.
  const inheritedFrom = roles?.find((r) => r.id === parentId);
  const inherited = new Set(
    inheritedFrom
      ? (inheritedFrom.effective_permissions ?? inheritedFrom.base_permissions)
      : [],
  );

  const renderParentPicker = () => (
    <div>
      <label className="block">
        <span className="text-xs font-medium text-[var(--text-secondary)]">
          Inherits from
        </span>
        <select
          value={parentId}
          onChange={(e) => setParentId(e.target.value)}
          className="mt-1 w-full rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 py-1.5 text-sm text-[var(--text-primary)]"
        >
          <option value="">Nothing — this role stands alone</option>
          {/* The owner role is left out: a child of Owner grants everything,
              which is Owner. The API refuses it for anyone but an owner; the
              list does not offer it to anyone. */}
          {(roles || [])
            .filter((r) => r.id !== editingId && !r.is_owner)
            .map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
        </select>
      </label>
      {inherited.size > 0 && (
        <p className="mt-1.5 rounded-[var(--radius-md)] bg-[var(--surface-sunken)] px-2.5 py-1.5 text-[11px] text-[var(--text-secondary)]">
          Also grants {inherited.size} permission
          {inherited.size === 1 ? "" : "s"} from{" "}
          <span className="font-semibold">{inheritedFrom?.name}</span>. Tick
          below only what this role adds.
        </p>
      )}
    </div>
  );

  const renderPermEditor = () => (
    <div className="mt-3">
      <PermissionChipGrid
        groups={groups}
        catalogue={catalogue}
        selected={selectedPerms}
        onToggle={togglePerm}
        onToggleGroup={toggleGroup}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={() => {
            setShowCreate(true);
            setEditingId(null);
            setName("");
            setDesc("");
            setParentId("");
            setSelectedPerms(new Set());
          }}
        >
          <Plus className="h-4 w-4" /> New Role
        </Button>
      </div>

      {/* Create Role Drawer */}
      <ActionDrawer
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create Custom Role"
        description="Define base permissions for a new role."
        icon={Shield}
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Role Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g., Shop Technician"
          />
          <Input
            label="Description"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="What this role does"
          />
          <label className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
              className="rounded border-[var(--border-default)]"
            />
            Admin access (can access Admin pages)
          </label>
          {renderParentPicker()}
          {renderPermEditor()}
          <div className="flex justify-end pt-2">
            <Button
              onClick={() => createMutation.mutate()}
              isLoading={createMutation.isPending}
            >
              Create Role ({selectedPerms.size} permissions)
            </Button>
          </div>
        </div>
      </ActionDrawer>

      {/* Edit Role Drawer */}
      <ActionDrawer
        isOpen={!!editingId}
        onClose={() => setEditingId(null)}
        title={`Edit Role: ${name}`}
        description="Modify permissions for this role."
        icon={Pencil}
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Role Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            label="Description"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
          />
          <label className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
              className="rounded border-[var(--border-default)]"
            />
            Admin access
          </label>
          {renderParentPicker()}
          {renderPermEditor()}
          <div className="flex justify-end pt-2">
            <Button
              onClick={() =>
                updateMutation.mutate({
                  id: editingId!,
                  data: {
                    name,
                    description: desc || undefined,
                    base_permissions: Array.from(selectedPerms),
                    is_admin: isAdmin,
                    parent_role_id: parentId || null,
                  },
                })
              }
              isLoading={updateMutation.isPending}
            >
              Save Changes ({selectedPerms.size} permissions)
            </Button>
          </div>
        </div>
      </ActionDrawer>

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : (
        <div className="space-y-2">
          {(roles || []).map((r) => (
            <Card key={r.id} padding="sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield
                    className={`h-4 w-4 ${r.is_owner ? "text-[var(--text-danger)]" : r.is_admin ? "text-[var(--text-warning)]" : "text-[var(--text-tertiary)]"}`}
                  />
                  <span className="text-sm font-semibold text-[var(--text-primary)]">
                    {r.name}
                  </span>
                  {r.is_system && <Badge variant="default">System</Badge>}
                  {r.is_owner && <Badge variant="danger">Owner</Badge>}
                  {r.is_admin && !r.is_owner && (
                    <Badge variant="warning">Admin</Badge>
                  )}
                  <span className="text-[10px] text-[var(--text-tertiary)]">
                    {/* The total, where a parent adds to it. Showing
                        base_permissions alone understates what the role grants
                        — which is exactly the gap inheritance introduces. */}
                    {r.effective_permissions
                      ? `${r.effective_permissions.length} permissions (${r.base_permissions.length} its own)`
                      : `${r.base_permissions.length} permissions`}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {!r.is_owner && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEdit(r)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {!r.is_owner && !r.is_system && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startDelete(r)}
                      title="Delete role"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-[var(--text-danger)]" />
                    </Button>
                  )}
                </div>
              </div>
              {r.description && (
                <p className="text-xs text-[var(--text-secondary)] mt-1 ml-6">
                  {r.description}
                </p>
              )}
              {r.parent_role_id && (
                <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5 ml-6">
                  Inherits from{" "}
                  <span className="font-medium">
                    {(roles || []).find((o) => o.id === r.parent_role_id)
                      ?.name || "a role you cannot see"}
                  </span>
                </p>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Reassignment, not cascade. When a role is held, the API refuses with
          a count and we ask where those people should go — otherwise deleting
          it would silently drop them to their legacy role. */}
      <ActionDrawer
        isOpen={deleting !== null}
        onClose={() => {
          setDeleting(null);
          setReplacementId("");
        }}
        title="Delete role"
        description={
          deleting ? `Remove "${deleting.name}" from this organization.` : ""
        }
        icon={Trash2}
        size="sm"
      >
        {holderCount > 0 ? (
          <div className="space-y-4">
            <div className="bg-[var(--surface-sunken)] rounded-[var(--radius-md)] p-3">
              <p className="text-sm text-[var(--text-primary)]">
                <span className="font-semibold">
                  {holderCount}{" "}
                  {holderCount === 1 ? "person holds" : "people hold"}
                </span>{" "}
                this role. Choose the role they should move to.
              </p>
              <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
                Without a replacement they would lose everything this role
                grants.
              </p>
            </div>

            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)]">
                Move them to
              </label>
              <select
                value={replacementId}
                onChange={(e) => setReplacementId(e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] text-[var(--text-primary)]"
              >
                <option value="">Select a role…</option>
                {(roles || [])
                  .filter((r) => r.id !== deleting?.id)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex justify-end gap-2 border-t border-[var(--border-subtle)] pt-4">
              <Button variant="ghost" onClick={() => setDeleting(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={!replacementId || deleteMutation.isPending}
                onClick={() =>
                  deleting &&
                  deleteMutation.mutate({
                    id: deleting.id,
                    replacement: replacementId,
                  })
                }
              >
                Reassign and delete
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-6 text-center">
            <LoadingSpinner />
            <p className="text-xs text-[var(--text-tertiary)] mt-2">
              Checking who holds this role…
            </p>
          </div>
        )}
      </ActionDrawer>
    </div>
  );
}

// ==========================================================================
// PERMISSION SETS TAB
// ==========================================================================

function PermissionSetsTab() {
  const qc = useQueryClient();
  const { data: sets, isLoading } = useQuery<PermSet[]>({
    queryKey: ["permission-sets"],
    queryFn: async () => (await apiClient.get("/api/v1/permissions/sets")).data,
  });

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [selectedPerms, setSelectedPerms] = useState<Set<string>>(new Set());

  const createMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post("/api/v1/permissions/sets", {
          name,
          description: desc || undefined,
          permissions: Array.from(selectedPerms),
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["permission-sets"] });
      setShowCreate(false);
      setName("");
      setDesc("");
      setSelectedPerms(new Set());
      toast.success("Permission set created");
    },
    onError: () => toast.error("Failed to create permission set"),
  });

  // Sets are additive, so deletion needs no replacement — losing one
  // narrows access rather than stranding anyone. The API strips the id from
  // every holder's permission_set_ids, which JSONB would otherwise keep
  // pointing at a row that no longer exists.
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/permissions/sets/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["permission-sets"] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["user-effective-perms"] });
      toast.success("Permission set deleted");
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error("Failed to delete permission set", msg || "");
    },
  });

  const { groups, catalogue } = usePermissionCatalogue();

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <p className="text-sm text-[var(--text-secondary)] max-w-lg">
          Permission sets are additive bundles that can be stacked on any user's
          role. Use them for cross-functional capabilities like "Credit Account
          Access" or "Reporting".
        </p>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" /> New Set
        </Button>
      </div>

      <ActionDrawer
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create Permission Set"
        description="Bundle permissions for cross-functional capabilities."
        icon={ShieldCheck}
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Set Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g., Credit Account Access"
          />
          <Input
            label="Description"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="What this set grants"
          />
          <PermissionChipGrid
            groups={groups}
            catalogue={catalogue}
            selected={selectedPerms}
            onToggle={(perm) =>
              setSelectedPerms((prev) => {
                const next = new Set(prev);
                if (next.has(perm)) next.delete(perm);
                else next.add(perm);
                return next;
              })
            }
          />
          <div className="flex justify-end pt-2">
            <Button
              onClick={() => createMutation.mutate()}
              isLoading={createMutation.isPending}
            >
              Create Set ({selectedPerms.size} permissions)
            </Button>
          </div>
        </div>
      </ActionDrawer>

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : (sets || []).length > 0 ? (
        <div className="space-y-2">
          {(sets || []).map((s) => (
            <Card key={s.id} padding="sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-[var(--color-interactive-primary)]" />
                    <span className="text-sm font-semibold text-[var(--text-primary)]">
                      {s.name}
                    </span>
                    <span className="text-[10px] text-[var(--text-tertiary)]">
                      {s.permissions.length} permissions
                    </span>
                  </div>
                  {s.description && (
                    <p className="text-xs text-[var(--text-secondary)] mt-0.5 ml-6">
                      {s.description}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (
                      confirm(
                        `Delete "${s.name}"? Anyone holding it loses the permissions it grants.`,
                      )
                    ) {
                      deleteMutation.mutate(s.id);
                    }
                  }}
                  title="Delete permission set"
                >
                  <Trash2 className="h-3.5 w-3.5 text-[var(--text-danger)]" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2 ml-6">
                {s.permissions.map((p) => (
                  <span key={p} className="micro-badge micro-badge-gray">
                    {p}
                  </span>
                ))}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <ShieldCheck />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No permission sets
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Create a set to bundle permissions for cross-functional users.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

// ==========================================================================
// TEAMS TAB
// ==========================================================================

function TeamsTab() {
  const qc = useQueryClient();
  const { data: teams, isLoading } = useQuery<Team[]>({
    queryKey: ["teams"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/permissions/teams")).data,
  });
  const { data: userData } = useQuery<PaginatedResponse<User>>({
    queryKey: ["admin-users"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/users?page_size=100")).data,
  });

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [managerId, setManagerId] = useState("");

  const createMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post("/api/v1/permissions/teams", {
          name,
          description: desc || undefined,
          manager_id: managerId || undefined,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teams"] });
      setShowCreate(false);
      setName("");
      setDesc("");
      setManagerId("");
      toast.success("Team created");
    },
    onError: () => toast.error("Failed to create team"),
  });

  const users = userData?.items || [];

  // Teams were create-only until FS-007a — a typo in a name was permanent.
  const [editing, setEditing] = useState<Team | null>(null);
  const [editName, setEditName] = useState("");
  const [editManagerId, setEditManagerId] = useState("");

  const updateMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.put(`/api/v1/permissions/teams/${editing?.id}`, {
          name: editName,
          manager_id: editManagerId || undefined,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teams"] });
      setEditing(null);
      toast.success("Team updated");
    },
    onError: () => toast.error("Failed to update team"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/permissions/teams/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teams"] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Team deleted");
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error("Failed to delete team", msg || "");
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <p className="text-sm text-[var(--text-secondary)] max-w-lg">
          Teams group users for visibility scoping. Team members with "Team"
          visibility can see records owned by any team member. Managers can see
          their team's records.
        </p>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" /> New Team
        </Button>
      </div>

      <ActionDrawer
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create Team"
        description="Group users for visibility scoping."
        icon={Users2}
        size="sm"
      >
        <div className="space-y-4">
          <Input
            label="Team Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g., Shop Crew"
          />
          <div>
            <label className="block text-[13px] font-medium text-[var(--text-primary)]">
              Manager
            </label>
            <select
              value={managerId}
              onChange={(e) => setManagerId(e.target.value)}
              className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
            >
              <option value="">No manager</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Description"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Optional"
          />
          <div className="flex justify-end pt-2">
            <Button
              onClick={() => createMutation.mutate()}
              isLoading={createMutation.isPending}
            >
              Create Team
            </Button>
          </div>
        </div>
      </ActionDrawer>

      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : (teams || []).length > 0 ? (
        <div className="space-y-2">
          {(teams || []).map((t) => (
            <Card key={t.id} padding="sm">
              <div className="flex items-center gap-2">
                <Users2 className="h-4 w-4 text-[var(--text-tertiary)]" />
                <span className="text-sm font-semibold text-[var(--text-primary)]">
                  {t.name}
                </span>
                {t.manager_id && (
                  <span className="text-[10px] text-[var(--text-tertiary)]">
                    Manager:{" "}
                    {users.find((u) => u.id === t.manager_id)?.full_name || "—"}
                  </span>
                )}
                <div className="ml-auto flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditing(t);
                      setEditName(t.name);
                      setEditManagerId(t.manager_id || "");
                    }}
                    title="Rename team"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (
                        confirm(
                          `Delete "${t.name}"? Its members will be unassigned.`,
                        )
                      ) {
                        deleteMutation.mutate(t.id);
                      }
                    }}
                    title="Delete team"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-[var(--text-danger)]" />
                  </Button>
                </div>
              </div>
              {t.description && (
                <p className="text-xs text-[var(--text-secondary)] mt-0.5 ml-6">
                  {t.description}
                </p>
              )}
            </Card>
          ))}

          <ActionDrawer
            isOpen={editing !== null}
            onClose={() => setEditing(null)}
            title="Edit team"
            description="Rename the team or change who manages it."
            icon={Users2}
            size="sm"
          >
            <div className="space-y-4">
              <Input
                label="Team Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
              />
              <div>
                <label className="block text-[13px] font-medium text-[var(--text-primary)]">
                  Manager
                </label>
                <select
                  value={editManagerId}
                  onChange={(e) => setEditManagerId(e.target.value)}
                  className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
                >
                  <option value="">No manager</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.full_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2 border-t border-[var(--border-subtle)] pt-4">
                <Button variant="ghost" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => updateMutation.mutate()}
                  isLoading={updateMutation.isPending}
                  disabled={!editName.trim()}
                >
                  Save
                </Button>
              </div>
            </div>
          </ActionDrawer>
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <Users2 />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No teams
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Create teams to manage visibility scoping.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

// ==========================================================================
// USER PROFILE FIELDS (sales & rate inline editor)
// ==========================================================================

function UserProfileFields({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const { data: user } = useQuery<{
    can_sell: boolean;
    default_commission_rate: number | null;
    hourly_rate: number | null;
  }>({
    queryKey: ["user-profile", userId],
    queryFn: async () => (await apiClient.get(`/api/v1/users/${userId}`)).data,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.put(`/api/v1/users/${userId}`, data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user-profile", userId] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("User updated");
    },
    onError: () => toast.error("Failed to update user"),
  });

  if (!user) return null;

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
        Rates & Sales
      </h4>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <label className="block text-[13px] font-medium text-[var(--text-primary)]">
            Hourly Rate ($)
          </label>
          <input
            type="number"
            step="0.01"
            defaultValue={user.hourly_rate ?? ""}
            onBlur={(e) => {
              const val = e.target.value ? parseFloat(e.target.value) : null;
              if (val !== user.hourly_rate)
                updateMutation.mutate({ hourly_rate: val });
            }}
            placeholder="0.00"
            className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
          />
        </div>
        <div className="flex flex-col justify-end">
          <label className="flex items-center gap-2 text-sm text-[var(--text-primary)] cursor-pointer h-[38px]">
            <input
              type="checkbox"
              checked={user.can_sell}
              onChange={(e) =>
                updateMutation.mutate({ can_sell: e.target.checked })
              }
              className="rounded border-[var(--border-default)]"
            />
            Can sell equipment
          </label>
        </div>
        {user.can_sell && (
          <div>
            <label className="block text-[13px] font-medium text-[var(--text-primary)]">
              Default Commission (%)
            </label>
            <input
              type="number"
              step="0.01"
              defaultValue={
                user.default_commission_rate != null
                  ? (Number(user.default_commission_rate) * 100).toFixed(2)
                  : ""
              }
              onBlur={(e) => {
                const val = e.target.value
                  ? parseFloat(e.target.value) / 100
                  : null;
                updateMutation.mutate({ default_commission_rate: val });
              }}
              placeholder="5.00"
              className="mt-1.5 block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagementPage;
